import { GAME_MODES } from '@blind-kings/game-core';
import { api } from 'aws-blocks';
import { Link, useNavigate } from 'react-router-dom';
import BkPage from '../../Components/ui/BkPage';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import useApiAction from '../../hooks/use-api-action';
import usePageMeta from '../../hooks/use-page-meta';
import { tierLabel } from '../../profile-frames/registry';
import { resolveOnlineGameId } from '../../utils/active-games';
import { DEFAULT_BOT_DIFFICULTY } from '../../bot/difficulty';

type ModeCardProps = {
  eyebrow: string;
  title: string;
  description: string;
  accent: 'gold' | 'accent' | 'danger' | 'muted';
  featured?: boolean;
  disabled?: boolean;
  badge?: string;
  onClick: () => void;
};

const accentRing: Record<ModeCardProps['accent'], string> = {
  gold: 'border-bk-gold/70 hover:border-bk-gold-bright hover:shadow-[0_0_28px_color-mix(in_srgb,var(--color-bk-gold)_35%,transparent)]',
  accent:
    'border-bk-accent/50 hover:border-bk-accent hover:shadow-[0_0_24px_color-mix(in_srgb,var(--color-bk-accent)_30%,transparent)]',
  danger:
    'border-bk-danger/50 hover:border-bk-danger-bright hover:shadow-[0_0_24px_color-mix(in_srgb,var(--color-bk-danger)_28%,transparent)]',
  muted: 'border-bk-border hover:border-bk-gold-dim/60',
};

const accentLabel: Record<ModeCardProps['accent'], string> = {
  gold: 'text-bk-gold-bright',
  accent: 'text-sky-300',
  danger: 'text-bk-danger-bright',
  muted: 'text-bk-muted',
};

function ModeCard({
  eyebrow,
  title,
  description,
  accent,
  featured,
  disabled,
  badge,
  onClick,
}: ModeCardProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        'group relative flex min-h-[7.5rem] flex-col items-start justify-between overflow-hidden border bg-bk-bg/80 p-4 text-left transition-all duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-bk-gold focus-visible:ring-offset-2 focus-visible:ring-offset-bk-bg',
        'disabled:pointer-events-none disabled:opacity-45',
        featured
          ? 'border-bk-gold bg-gradient-to-br from-bk-panel-raised via-bk-panel to-bk-bg shadow-[0_0_22px_color-mix(in_srgb,var(--color-bk-gold)_22%,transparent)]'
          : accentRing[accent],
      ].join(' ')}
    >
      <div
        className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-30 blur-2xl transition-opacity group-hover:opacity-55"
        style={{
          background:
            accent === 'gold'
              ? 'var(--color-bk-gold)'
              : accent === 'accent'
                ? 'var(--color-bk-accent)'
                : accent === 'danger'
                  ? 'var(--color-bk-danger)'
                  : 'var(--color-bk-border)',
        }}
      />
      <div className="relative w-full">
        <div className="flex items-start justify-between gap-2">
          <p
            className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${accentLabel[accent]}`}
          >
            {eyebrow}
          </p>
          {badge ? (
            <span className="shrink-0 border border-bk-gold-dim/50 bg-bk-panel-raised px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bk-gold">
              {badge}
            </span>
          ) : null}
        </div>
        <h3 className="mt-1 text-xl font-extrabold uppercase tracking-wider text-bk-text sm:text-2xl">
          {title}
        </h3>
      </div>
      <p className="relative mt-3 text-xs leading-snug text-bk-muted sm:text-sm">
        {description}
      </p>
    </button>
  );
}

export default function PlayHub() {
  usePageMeta({
    title: 'Play',
    description:
      'Choose a Blind Kings game mode — local, online, or ranked. Reveal, strike, and outwit your rival.',
    path: '/play',
  });

  const navigate = useNavigate();
  const { addSnackBar } = useSnackBarContext();
  const { currentUser, userData, openLoginModal } = useAuthContext();
  const [createLocalGame, loadingLocalGame] = useApiAction({
    action: async () => api.createGame(GAME_MODES.LOCAL_GAME),
    reloadUserData: true,
  });
  const [createBotGame, loadingBotGame] = useApiAction({
    action: async () =>
      api.createGame(GAME_MODES.AI_GAME, {
        botDifficulty: DEFAULT_BOT_DIFFICULTY,
      }),
    reloadUserData: true,
  });

  const botGameId = userData?.currentBotGameId;
  const localGameId = userData?.currentLocalGameId;
  const onlineGameId = resolveOnlineGameId(userData);
  const onlineMode = userData?.currentOnlineGameMode as string | undefined;
  const onlineIsRanked = onlineMode === GAME_MODES.RANKED_GAME;
  const onlineIsOnline =
    !onlineGameId ||
    onlineMode === GAME_MODES.ONLINE_GAME ||
    (!onlineMode && !!onlineGameId);

  function go(path: string) {
    navigate(path);
  }

  function requireAuthThen(path: string) {
    if (!currentUser) {
      openLoginModal();
      return;
    }
    go(path);
  }

  async function handleLocalGameClick() {
    if (localGameId) {
      go(`/play/${localGameId}`);
      return;
    }
    if (!currentUser) {
      openLoginModal();
      return;
    }
    try {
      const createGameData = (await createLocalGame()) as { gameId?: string } | undefined;
      const gameId = createGameData?.gameId;
      if (gameId) go(`/play/${gameId}`);
    } catch (err) {
      console.error(err);
      addSnackBar({
        title: 'Error Making Game',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 2500,
      });
    }
  }

  function handleOnlineClick() {
    if (onlineGameId) {
      go(`/play/${onlineGameId}`);
      return;
    }
    requireAuthThen(`/queue/${GAME_MODES.ONLINE_GAME}`);
  }

  function handleRankedClick() {
    if (onlineGameId) {
      go(`/play/${onlineGameId}`);
      return;
    }
    requireAuthThen(`/queue/${GAME_MODES.RANKED_GAME}`);
  }

  async function handleStartBot() {
    if (botGameId) {
      go(`/play/${botGameId}`);
      return;
    }
    if (!currentUser) {
      openLoginModal();
      return;
    }
    try {
      const createGameData = (await createBotGame()) as
        | { gameId?: string }
        | undefined;
      const gameId = createGameData?.gameId;
      if (gameId) go(`/play/${gameId}`);
    } catch (err) {
      console.error(err);
      addSnackBar({
        title: 'Error Making Game',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 2500,
      });
    }
  }

  return (
    <BkPage narrow fullHeight className="justify-center">
      <div className="mb-8 space-y-2 text-center">
        <h1 className="text-3xl font-extrabold uppercase tracking-wider text-bk-gold md:text-4xl">
          Enter the Arena
        </h1>
        <p className="text-sm text-bk-muted md:text-base">
          Choose how you want to clash — every mode uses the same ruthless board.
        </p>
        <p className="pt-1">
          <Link
            to="/leaderboard"
            className="text-xs font-bold uppercase tracking-wider text-bk-gold hover:text-bk-gold-bright"
          >
            View season leaderboard →
          </Link>
        </p>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <ModeCard
          featured
          accent="gold"
          eyebrow="Quick match"
          title={onlineGameId && onlineIsOnline ? 'Continue Online' : 'Online'}
          description={
            onlineGameId && !onlineIsOnline
              ? 'Finish your ranked match before queuing online.'
              : 'Find a rival instantly. Timed turns. Full rewards.'
          }
          badge={onlineGameId && onlineIsOnline ? 'Live' : undefined}
          disabled={!!onlineGameId && !onlineIsOnline}
          onClick={handleOnlineClick}
        />
        <ModeCard
          accent="danger"
          eyebrow="High stakes"
          title={onlineGameId && onlineIsRanked ? 'Continue Ranked' : 'Ranked'}
          description={
            onlineGameId && !onlineIsRanked
              ? 'Finish your online match before queuing ranked.'
              : userData?.ranked?.elo != null
                ? `${tierLabel(userData.ranked.tier)} · ${userData.ranked.elo} Elo · 15% reward boost.`
                : 'Skill ladder. 15% reward boost. Climb the season ranks.'
          }
          badge={onlineGameId && onlineIsRanked ? 'Live' : undefined}
          disabled={!!onlineGameId && !onlineIsRanked}
          onClick={handleRankedClick}
        />
        <ModeCard
          accent="accent"
          eyebrow="Practice"
          title={
            loadingLocalGame
              ? 'Creating…'
              : localGameId
                ? 'Continue Local'
                : 'Local'
          }
          description="Hot-seat both armies on one device. Perfect for teaching the rules."
          badge={localGameId ? 'Resume' : undefined}
          disabled={loadingLocalGame}
          onClick={() => void handleLocalGameClick()}
        />
        <ModeCard
          accent="accent"
          eyebrow="Solo"
          title={
            loadingBotGame
              ? 'Creating…'
              : botGameId
                ? 'Continue Bot'
                : 'Bot'
          }
          description={
            botGameId
              ? 'Return to your match against Blind Bot.'
              : 'Play Blind Bot at full strength.'
          }
          badge={botGameId ? 'Resume' : undefined}
          disabled={loadingBotGame}
          onClick={() => void handleStartBot()}
        />
      </div>
    </BkPage>
  );
}
